import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/lib/services";
import { blankTask, cleanTask, taskTotal } from "@/lib/tasks";
import { fmtDuration } from "@/lib/time";
import { useRoleNames, useStatuses } from "@/hooks/useLookups";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskBuilder from "@/components/jobs/TaskBuilder";
import TemplateDates from "@/components/templates/TemplateDates";

export default function TemplateDialog({ template, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", service: "other", description: "", tasks: [] });
  const editing = Boolean(template?.id);
  const roleNames = useRoleNames();
  const statuses = useStatuses();

  useEffect(() => {
    if (template) setF({ name: "", service: "other", description: "", tasks: [blankTask()], ...template });
  }, [template]);

  const save = async (e) => {
    e.preventDefault();
    const body = { name: f.name, service: f.service, description: f.description, tasks: f.tasks.map(cleanTask),
      start_rule: f.start_rule || null, internal_rule: f.internal_rule || null, deadline_rule: f.deadline_rule || null };
    try {
      if (editing) await api.put(`/templates/${template.id}`, body);
      else await api.post("/templates", body);
      toast.success("Template saved");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const total = f.tasks.reduce((a, t) => a + taskTotal(t), 0);
  return (
    <Dialog open={Boolean(template)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 w-[95vw] max-w-6xl h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription>Tasks go on the planner and are assigned to the person in that role. Subtasks are ticked off to track progress; their times add up to the task's time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Template name</Label>
              <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl bg-white border-[#103080]/20 h-10" data-testid="template-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Service</Label>
              <Select value={f.service} onValueChange={(v) => setF({ ...f, service: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#103080]/20 h-10" data-testid="template-service-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <TemplateDates value={f} onChange={setF} />
          <TaskBuilder tasks={f.tasks} onChange={(tasks) => setF({ ...f, tasks })} roleNames={roleNames} statuses={statuses} testPrefix="template" />
          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-[#103080] font-semibold" data-testid="template-total">{f.tasks.length} tasks · {fmtDuration(total)}</p>
            <button type="submit" data-testid="template-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">Save template</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
